import React, { useState } from "react";
import { Button, Empty, List, message, Tabs, Tag, Spin, Popconfirm } from "antd";
import { CloseOutlined, ExclamationCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { alertServices } from "~/services/alert/alert.service";
import { TAlert } from "~/types/alerts";
import { capitalize } from "~/utils";
import { Link } from "react-router-dom";
import { useAuth } from "~/store";
import { Role } from "~/utils/constants";

export const AlertsManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<"open" | "dismissed">("open");

  const { data: openAlerts, isLoading: loadingOpen } = useQuery({
    queryKey: ["alerts", "open"],
    queryFn: () => alertServices.getAlerts({ page: 1, pageSize: 100, status: "open" }),
  });

  const { data: dismissedAlerts, isLoading: loadingDismissed } = useQuery({
    queryKey: ["alerts", "dismissed"],
    queryFn: () => alertServices.getAlerts({ page: 1, pageSize: 100, status: "dismissed" }),
    enabled: activeTab === "dismissed",
  });

  const dismissMutation = useMutation({
    mutationFn: (alertId: string) => alertServices.dismissAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert"] });
      message.success("Alert dismissed successfully");
    },
    onError: () => {
      message.error("Failed to dismiss alert");
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () => alertServices.regenerateAlerts(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["alert"] });
      message.success(`Successfully updated ${data.updated} alert(s) with correct data`);
    },
    onError: () => {
      message.error("Failed to regenerate alerts");
    },
  });

  const getTagColor = (type: string) => {
    switch (type) {
      case "DUE_IN_1_DAY":
      case "OVERDUE":
        return "red";
      case "DUE_IN_2_DAYS":
        return "orange";
      case "DUE_IN_3_DAYS":
        return "yellow";
      default:
        return "blue";
    }
  };

  const renderAlertItem = (alert: TAlert, showDismiss: boolean = true) => (
    <List.Item
      key={alert.id}
      actions={
        showDismiss
          ? [
              <Popconfirm
                title="Dismiss this alert?"
                description="You can view dismissed alerts in the Dismissed tab."
                onConfirm={() => dismissMutation.mutate(alert.id)}
                okText="Dismiss"
                cancelText="Cancel"
              >
                <Button
                  type="text"
                  danger
                  icon={<CloseOutlined />}
                  size="small"
                  loading={dismissMutation.isPending}
                >
                  Dismiss
                </Button>
              </Popconfirm>,
            ]
          : []
      }
    >
      <List.Item.Meta
        avatar={<ExclamationCircleOutlined style={{ fontSize: 24, color: getTagColor(alert.type) === "red" ? "#ff4d4f" : getTagColor(alert.type) === "orange" ? "#ff7a45" : "#faad14" }} />}
        title={
          <div className="flex items-center gap-2">
            <Tag color={getTagColor(alert.type)}>{capitalize(alert.type)}</Tag>
            {alert.serviceTicket && (
              <Link
                to={`/bookings/${alert.serviceTicket.jobTicketId}`}
                className="text-blue-600 hover:text-blue-800"
              >
                {alert.serviceTicket.serial || "View Service"}
              </Link>
            )}
          </div>
        }
        description={
          <div>
            <div className="mb-1">{alert.message}</div>
            <div className="text-xs text-gray-500">
              Created: {new Date(alert.createdAt).toLocaleString()}
              {alert.dismissedAt && (
                <span className="ml-4">
                  Dismissed: {new Date(alert.dismissedAt).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        }
      />
    </List.Item>
  );

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Alerts Management</h2>
        <div className="flex gap-2">
          {(user?.role === Role.SUPER_ADMIN || user?.role === Role.SYSTEM_ADMIN) && (
            <Button
              icon={<ReloadOutlined />}
              onClick={() => regenerateMutation.mutate()}
              loading={regenerateMutation.isPending}
            >
              Refresh Alert Messages
            </Button>
          )}
          <Link to="/">
            <Button>Back to Dashboard</Button>
          </Link>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as "open" | "dismissed")}
        items={[
          {
            key: "open",
            label: (
              <span>
                Open Alerts
                {openAlerts && (
                  <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                    {openAlerts.items.length}
                  </span>
                )}
              </span>
            ),
            children: (
              <div>
                {loadingOpen ? (
                  <div className="flex justify-center py-8">
                    <Spin size="large" />
                  </div>
                ) : !openAlerts || openAlerts.items.length === 0 ? (
                  <Empty
                    description="No open alerts"
                    className="py-8"
                  />
                ) : (
                  <List
                    dataSource={openAlerts.items}
                    renderItem={(alert) => renderAlertItem(alert, true)}
                    pagination={
                      openAlerts.items.length > 10
                        ? {
                            pageSize: 10,
                            showSizeChanger: false,
                          }
                        : false
                    }
                  />
                )}
              </div>
            ),
          },
          {
            key: "dismissed",
            label: (
              <span>
                Dismissed (Last 30 days)
                {dismissedAlerts && (
                  <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                    {dismissedAlerts.items.length}
                  </span>
                )}
              </span>
            ),
            children: (
              <div>
                {loadingDismissed ? (
                  <div className="flex justify-center py-8">
                    <Spin size="large" />
                  </div>
                ) : !dismissedAlerts || dismissedAlerts.items.length === 0 ? (
                  <Empty
                    description="No dismissed alerts in the last 30 days"
                    className="py-8"
                  />
                ) : (
                  <List
                    dataSource={dismissedAlerts.items}
                    renderItem={(alert) => renderAlertItem(alert, false)}
                    pagination={
                      dismissedAlerts.items.length > 10
                        ? {
                            pageSize: 10,
                            showSizeChanger: false,
                          }
                        : false
                    }
                  />
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};